import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationsApi } from '../../services/enterpriseApi';
import Modal from '../../components/common/Modal';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { LoadingScreen } from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import {
  Bell,
  Mail,
  Smartphone,
  Webhook,
  CheckCircle,
  Clock,
  AlertCircle,
  Settings,
  Plus,
  Trash2,
  TrendingUp,
} from 'lucide-react';

export default function NotificationsCenter() {
  const [showPreferences, setShowPreferences] = useState(false);
  const [showCreateRule, setShowCreateRule] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<any>(null);
  const queryClient = useQueryClient();

  // Preferences state
  const [preferences, setPreferences] = useState({
    email_enabled: true,
    sms_enabled: false,
    push_enabled: true,
    webhook_enabled: false,
    quiet_hours: { start: '22:00', end: '08:00' },
  });

  // Alert rule state
  const [newRule, setNewRule] = useState({
    name: '',
    event_type: '',
    channels: [] as string[],
    cooldown_minutes: 60,
    conditions: {},
  });

  const { data: notifications, isLoading: notificationsLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationsApi.getAll({ limit: 50 }),
  });

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['notification-stats'],
    queryFn: () => notificationsApi.getStats(),
  });

  const { data: userPreferences } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: () => notificationsApi.getPreferences(),
  });

  // Sync server preferences into local state once fetched (must be in useEffect to avoid render-loop)
  useEffect(() => {
    if (userPreferences?.data) {
      setPreferences(userPreferences.data);
    }
  }, [userPreferences]);

  const { data: alertRules } = useQuery({
    queryKey: ['alert-rules'],
    queryFn: () => notificationsApi.getAlertRules(),
  });

  const markAsReadMutation = useMutation({
    mutationFn: (id: string) => notificationsApi.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notification-stats'] });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: () => notificationsApi.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notification-stats'] });
    },
  });

  const updatePreferencesMutation = useMutation({
    mutationFn: (prefs: any) => notificationsApi.updatePreferences(prefs),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
      setShowPreferences(false);
    },
  });

  const createAlertRuleMutation = useMutation({
    mutationFn: (rule: any) => notificationsApi.createAlertRule(rule),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
      setShowCreateRule(false);
      setNewRule({
        name: '',
        event_type: '',
        channels: [],
        cooldown_minutes: 60,
        conditions: {},
      });
    },
  });

  const deleteAlertRuleMutation = useMutation({
    mutationFn: (id: string) => notificationsApi.deleteAlertRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
    },
  });

  if (notificationsLoading || statsLoading) {
    return <LoadingScreen message="Loading notifications..." />;
  }

  // Backend returns array for notifications; compute unread from it
  const notifList: any[] = Array.isArray(notifications?.data)
    ? notifications.data
    : notifications?.data?.notifications ?? [];
  const unreadCount = notifList.filter((n: any) => !n.read_at).length;

  const statCards = [
    {
      title: 'Total Sent',
      value: stats?.data?.totalSent || 0,
      icon: Bell,
      color: 'bg-primary-500',
    },
    {
      title: 'Unread',
      value: unreadCount,
      icon: AlertCircle,
      color: 'bg-yellow-500',
    },
    {
      title: 'Delivered',
      value: stats?.data?.delivered || 0,
      icon: CheckCircle,
      color: 'bg-green-500',
    },
    {
      title: 'Failed',
      value: stats?.data?.failed || 0,
      icon: TrendingUp,
      color: 'bg-blue-500',
    },
  ];

  const channelIcons = {
    email: Mail,
    sms: Smartphone,
    push: Bell,
    webhook: Webhook,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold text-gray-900 dark:text-white">
            Notifications Center
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Manage your notifications and alert preferences
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowPreferences(true)}>
            <Settings className="w-4 h-4 mr-2" />
            Preferences
          </Button>
          <Button onClick={() => markAllAsReadMutation.mutate()}>
            Mark All as Read
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{stat.title}</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stat.value}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Notifications List */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Recent Notifications</h2>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {notifList.length} notifications
          </span>
        </div>

        <div className="space-y-3 max-h-[500px] overflow-y-auto">
          {notifList.length > 0 ? (
            notifList.map((notification: any) => (
              <div
                key={notification.id}
                className={`p-4 border rounded-lg transition-colors ${
                  notification.read_at
                    ? 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
                    : 'border-primary-200 bg-primary-50 dark:border-primary-800 dark:bg-primary-900/20'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Bell className={`w-4 h-4 ${notification.read_at ? 'text-gray-400' : 'text-primary-600'}`} />
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {notification.subject ?? notification.title ?? notification.event_type}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                      {notification.body ?? notification.message}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                      <span className="flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {new Date(notification.sent_at ?? notification.created_at).toLocaleString()}
                      </span>
                      <span className="capitalize">{notification.event_type ?? notification.type}</span>
                      <div className="flex items-center gap-1">
                        {notification.channel && (() => {
                          const Icon = channelIcons[notification.channel as keyof typeof channelIcons];
                          return Icon ? <Icon className="w-3 h-3" /> : null;
                        })()}
                      </div>
                    </div>
                  </div>
                  {!notification.read_at && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => markAsReadMutation.mutate(notification.id)}
                    >
                      Mark Read
                    </Button>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-300">No notifications</p>
            </div>
          )}
        </div>
      </Card>

      {/* Alert Rules */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Alert Rules</h2>
          <Button onClick={() => setShowCreateRule(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create Rule
          </Button>
        </div>

        {showCreateRule && (
          <div className="mb-6 p-4 border border-primary-200 bg-primary-50 rounded-lg">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Create New Alert Rule</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rule Name
                </label>
                <input
                  type="text"
                  value={newRule.name}
                  onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="High confidence pest detection"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Type
                </label>
                <select
                  value={newRule.event_type}
                  onChange={(e) => setNewRule({ ...newRule, event_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select event type</option>
                  <option value="mission.completed">Mission Completed</option>
                  <option value="analysis.high_confidence">High Confidence Analysis</option>
                  <option value="alert.critical">Critical Alert</option>
                  <option value="decision.pending_review">Decision Pending Review</option>
                  <option value="review.certified">Review Certified</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notification Channels
                </label>
                <div className="flex gap-4">
                  {['email', 'sms', 'push', 'webhook'].map((channel) => (
                    <label key={channel} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newRule.channels.includes(channel)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewRule({ ...newRule, channels: [...newRule.channels, channel] });
                          } else {
                            setNewRule({
                              ...newRule,
                              channels: newRule.channels.filter((c) => c !== channel),
                            });
                          }
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm capitalize">{channel}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cooldown (minutes)
                </label>
                <input
                  type="number"
                  value={newRule.cooldown_minutes}
                  onChange={(e) =>
                    setNewRule({ ...newRule, cooldown_minutes: parseInt(e.target.value) })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  min="0"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => createAlertRuleMutation.mutate(newRule)}
                  disabled={
                    !newRule.name ||
                    !newRule.event_type ||
                    newRule.channels.length === 0 ||
                    createAlertRuleMutation.isPending
                  }
                >
                  {createAlertRuleMutation.isPending ? 'Creating...' : 'Create Rule'}
                </Button>
                <Button variant="outline" onClick={() => setShowCreateRule(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {alertRules?.data?.rules && alertRules.data.rules.length > 0 ? (
            alertRules.data.rules.map((rule: any) => (
              <div key={rule.id} className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{rule.name}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                      Event: <span className="font-medium">{rule.event_type}</span>
                    </p>
                    <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                      <span>Cooldown: {rule.cooldown_minutes}m</span>
                      <div className="flex items-center gap-1">
                        {rule.channels?.map((channel: string) => {
                          const Icon = channelIcons[channel as keyof typeof channelIcons];
                          return Icon ? <Icon key={channel} className="w-3 h-3" /> : null;
                        })}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteAlertRuleMutation.mutate(rule.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <AlertCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600 dark:text-gray-300">No alert rules configured</p>
            </div>
          )}
        </div>
      </Card>

      {/* Preferences Modal */}
      {showPreferences && (
        <Modal onClose={() => setShowPreferences(false)} raw>
          <Card className="max-w-2xl w-full max-h-[calc(100dvh-2rem)] overflow-y-auto">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
              Notification Preferences
            </h2>

            <div className="space-y-6">
              {/* Channel Toggles */}
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Notification Channels</h3>
                <div className="space-y-3">
                  {[
                    { key: 'email_enabled', label: 'Email', icon: Mail },
                    { key: 'sms_enabled', label: 'SMS', icon: Smartphone },
                    { key: 'push_enabled', label: 'Push', icon: Bell },
                    { key: 'webhook_enabled', label: 'Webhook', icon: Webhook },
                  ].map(({ key, label, icon: Icon }) => (
                    <label key={key} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5 text-gray-600" />
                        <span className="font-medium text-gray-900 dark:text-white">{label}</span>
                      </div>
                      <input
                        type="checkbox"
                        checked={preferences[key as keyof typeof preferences] as boolean}
                        onChange={(e) =>
                          setPreferences({ ...preferences, [key]: e.target.checked })
                        }
                        className="w-5 h-5"
                      />
                    </label>
                  ))}
                </div>
              </div>

              {/* Quiet Hours */}
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Quiet Hours</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                  Don't send notifications during these hours
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Time
                    </label>
                    <input
                      type="time"
                      value={preferences.quiet_hours.start}
                      onChange={(e) =>
                        setPreferences({
                          ...preferences,
                          quiet_hours: { ...preferences.quiet_hours, start: e.target.value },
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Time
                    </label>
                    <input
                      type="time"
                      value={preferences.quiet_hours.end}
                      onChange={(e) =>
                        setPreferences({
                          ...preferences,
                          quiet_hours: { ...preferences.quiet_hours, end: e.target.value },
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t">
                <Button
                  onClick={() => updatePreferencesMutation.mutate(preferences)}
                  disabled={updatePreferencesMutation.isPending}
                >
                  {updatePreferencesMutation.isPending ? 'Saving...' : 'Save Preferences'}
                </Button>
                <Button variant="outline" onClick={() => setShowPreferences(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        </Modal>
      )}
    </div>
  );
}

// Made with Bob
